import React, { useEffect, useMemo, useRef, useState } from 'react';

import { GrupoDeAcesso, TelaInicialDisponivel } from '../../../api/gruposDeAcessoApi';
import { PAPEIS_DO_SISTEMA, papeisBloqueados } from '../pages/GruposDeAcessoPage';
import '../../../shared/ui/modal-close-button.css';
import '../pages/css/adminSistema.css';
import './css/grupoModal.css';

interface Props {
  /** `null` = criação. */
  grupo: GrupoDeAcesso | null;
  telas: TelaInicialDisponivel[];
  onFechar: () => void;
  onSalvar: (payload: Record<string, unknown>, id?: string) => void;
}

/**
 * Criação e edição de grupo de acesso (at-81, ADR 0044).
 *
 * ⚠️ Montagem CONDICIONAL pelo pai (`{aberto && <Modal/>}`), nunca prop `isOpen` com early
 * return: é o que faz o `useState` inicial ser a única fonte do rascunho e o "Cancelar" desfazer
 * de verdade — a armadilha da at-61.
 */
const GrupoDeAcessoModal: React.FC<Props> = ({ grupo, telas, onFechar, onSalvar }) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  const [nome, setNome] = useState(grupo?.nome ?? '');
  const [descricao, setDescricao] = useState(grupo?.descricao ?? '');
  const [papeis, setPapeis] = useState<string[]>(grupo?.papeis ?? []);
  const [telaInicial, setTelaInicial] = useState(grupo?.telaInicial ?? '');
  const [prioridade, setPrioridade] = useState(String(grupo?.prioridade ?? 0));
  const [ativo, setAtivo] = useState(grupo?.ativo ?? true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onFechar]);

  const bloqueados = useMemo(() => papeisBloqueados(papeis), [papeis]);

  const alternarPapel = (valor: string) => {
    setPapeis((atuais) =>
      atuais.includes(valor) ? atuais.filter((p) => p !== valor) : [...atuais, valor],
    );
  };

  const submeter = (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!nome.trim()) return;

    setSalvando(true);
    onSalvar(
      {
        nome: nome.trim(),
        descricao: descricao.trim() || undefined,
        papeis,
        telaInicial: telaInicial || null,
        // Prioridade só significa algo com tela definida — sem ela, grava 0 e não fica um
        // número solto no banco sugerindo uma configuração que não existe.
        prioridade: telaInicial ? Number(prioridade) || 0 : 0,
        ativo,
      },
      grupo?.id,
    );
    setSalvando(false);
  };

  return (
    <div
      className="grupo-modal-overlay"
      ref={overlayRef}
      onMouseDown={(evento) => {
        if (evento.target === overlayRef.current) onFechar();
      }}
    >
      <div className="grupo-modal" role="dialog" aria-modal="true">
        <div className="grupo-modal-header">
          <h2>{grupo ? `Editar "${grupo.nome}"` : 'Novo grupo de acesso'}</h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            ×
          </button>
        </div>

        <form onSubmit={submeter} style={{ display: 'contents' }}>
          <div className="grupo-modal-body">
            <div className="grupo-modal-campo">
              <label htmlFor="grupo-nome">Nome do grupo</label>
              <input
                id="grupo-nome"
                className="admin-input"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Consultoria de Relacionamento"
                maxLength={120}
                required
                autoFocus
              />
            </div>

            <div className="grupo-modal-campo">
              <label htmlFor="grupo-descricao">Para que serve (opcional)</label>
              <input
                id="grupo-descricao"
                className="admin-input"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Quem atende o associado e acompanha o comissionamento."
                maxLength={500}
              />
            </div>

            <div className="grupo-modal-secao">
              <h3 className="grupo-modal-secao-titulo">Perfis que este grupo concede</h3>
              <p className="grupo-modal-hint">
                Quem entrar no grupo soma estes perfis aos que já tem.
              </p>

              <div className="grupo-modal-perfis">
                {PAPEIS_DO_SISTEMA.map(({ valor, rotulo }) => {
                  const marcado = papeis.includes(valor);
                  const bloqueado = bloqueados.includes(valor);
                  const classes = [
                    'grupo-modal-perfil',
                    marcado ? 'grupo-modal-perfil-marcado' : '',
                    bloqueado ? 'grupo-modal-perfil-bloqueado' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <label
                      key={valor}
                      className={classes}
                      title={
                        bloqueado
                          ? 'Não pode ser combinado com o perfil financeiro já selecionado.'
                          : rotulo
                      }
                    >
                      <input
                        type="checkbox"
                        checked={marcado}
                        disabled={bloqueado}
                        onChange={() => alternarPapel(valor)}
                      />
                      {rotulo}
                    </label>
                  );
                })}
              </div>

              <p className="grupo-modal-contador">
                {papeis.length === 0
                  ? 'Nenhum perfil selecionado — o grupo pode ser salvo assim e receber perfis depois.'
                  : `${papeis.length} perfil(is) selecionado(s).`}
              </p>

              {bloqueados.length > 0 && (
                <p className="grupo-modal-hint grupo-modal-hint-alerta">
                  Contac, Consultor, Regional e Relacionamento não podem ser combinados entre si:
                  os quatro mostram os mesmos números de jeitos diferentes.
                </p>
              )}
            </div>

            <div className="grupo-modal-secao">
              <h3 className="grupo-modal-secao-titulo">Tela inicial</h3>
              <p className="grupo-modal-hint">
                Para onde a pessoa vai ao entrar. Sem escolher, o sistema decide pelos perfis dela,
                como sempre fez.
              </p>

              <div className="grupo-modal-duas-colunas">
                <div className="grupo-modal-campo">
                  <label htmlFor="grupo-tela">Tela</label>
                  <select
                    id="grupo-tela"
                    className="admin-select"
                    value={telaInicial}
                    onChange={(e) => setTelaInicial(e.target.value)}
                  >
                    <option value="">Escolher pela regra de perfil</option>
                    {telas.map((tela) => (
                      <option key={tela.path} value={tela.path}>
                        {tela.rotulo}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grupo-modal-campo">
                  <label htmlFor="grupo-prioridade">Prioridade</label>
                  <input
                    id="grupo-prioridade"
                    className="admin-input"
                    type="number"
                    min={0}
                    value={prioridade}
                    onChange={(e) => setPrioridade(e.target.value)}
                    /* Desabilitado sem tela: prioridade sozinha não decide nada, e um campo
                       editável que não faz efeito é pior que um campo ausente. */
                    disabled={!telaInicial}
                    title={
                      telaInicial
                        ? undefined
                        : 'Escolha uma tela inicial para definir a prioridade.'
                    }
                  />
                </div>
              </div>

              {telaInicial && (
                <p className="grupo-modal-hint">
                  Quem estiver em vários grupos com tela definida vai para a do maior número.
                </p>
              )}
            </div>

            <div className="grupo-modal-ativo">
              <label className="grupo-modal-ativo-linha">
                <input type="checkbox" checked={ativo} onChange={() => setAtivo((v) => !v)} />
                Grupo ativo
              </label>
              <p className="grupo-modal-hint">
                {ativo
                  ? 'As pessoas do grupo recebem os perfis acima a partir do próximo login delas.'
                  : 'Grupo inativo não concede perfil nenhum. As pessoas continuam vinculadas e voltam a receber quando ele for reativado.'}
              </p>
            </div>
          </div>

          <div className="grupo-modal-footer">
            <button type="button" className="admin-btn admin-btn-secondary" onClick={onFechar}>
              Cancelar
            </button>
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={salvando || !nome.trim()}
            >
              {salvando ? 'Salvando...' : 'Salvar grupo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GrupoDeAcessoModal;
